import React, { useState } from "react";
import { S3Client, PutObjectCommand } from "@aws-sdk/client-s3";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";

const REGION = "us-east-1";
const BUCKET = "lexiabucket";
const IDENTITY_POOL_ID = "us-east-1:51593b90-a74a-4ddb-8647-b551d15b1ea6";
const API_ENDPOINT = "https://2tjuygx4dqjno7rcplo2meacwq0dwwvq.lambda-url.us-east-1.on.aws";

const s3 = new S3Client({
  region: REGION,
  credentials: fromCognitoIdentityPool({
    client: new CognitoIdentityClient({ region: REGION }),
    identityPoolId: IDENTITY_POOL_ID,
  }),
});

// Diccionario de opciones
const OPCIONES_INSTANCIA = {
  "1": "1 instancia (Juzgado)",
  "2": "Apelación (Corte suprema)",
  "3": "Casación (Corte suprema)",
  "4": "Recurso de Agravio Constitucional (TC)",
};

// Estilo unificado para todas las secciones
const sectionStyle = {
  marginTop: 20, 
  border: "2px solid #2196F3", 
  padding: 20, 
  borderRadius: 8,
  backgroundColor: "#f8f9fa"
};

const sectionTitleStyle = {
  color: "#2196F3", 
  marginBottom: 20, 
  fontSize: "20px",
  margin: 0,
  paddingBottom: 10,
  borderBottom: "1px solid #e0e0e0"
};

export default function App() {
  const [files, setFiles] = useState([]);
  const [summary, setSummary] = useState("");
  const [instanciaId, setInstanciaId] = useState("");
  const [detail, setDetail] = useState(null);
  const [judgeProfile, setJudgeProfile] = useState(null);
  const [extractedJudgeName, setExtractedJudgeName] = useState(null);
  const [predictiveAnalysis, setPredictiveAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    setFiles(Array.from(e.target.files));
    setSummary("");
    setDetail(null);
    setJudgeProfile(null);
    setExtractedJudgeName(null);
    setPredictiveAnalysis(null);
    setError("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!instanciaId) {
      setError("❌ Debes seleccionar una instancia");
      return;
    }
    if (files.length === 0) {
      setError("❌ Selecciona al menos un PDF");
      return;
    }

    setLoading(true);
    setError("");
    setSummary("");
    setDetail(null);
    setJudgeProfile(null);
    setExtractedJudgeName(null);
    setPredictiveAnalysis(null);

    try {
      // 1) subir archivos a S3
      const uploads = await Promise.all(
        files.map(async (file) => {
          const key = `uploads/${Date.now()}_${file.name}`;
          const body = new Uint8Array(await file.arrayBuffer());
          await s3.send(
            new PutObjectCommand({
              Bucket: BUCKET,
              Key: key,
              Body: body,
              ContentType: file.type,
            })
          );
          return key;
        })
      );

      // 2) llamar a Lambda
      const resp = await fetch(API_ENDPOINT, {
        method: "POST",
        mode: "cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bucket: BUCKET,
          keys: uploads,
          instancia_id: instanciaId,
        }),
      });

      const payload = await resp.json();
      if (!resp.ok) throw new Error(payload.error || "Error en la API");

      console.log("=== RESPONSE PAYLOAD COMPLETO ===");
      console.log(JSON.stringify(payload, null, 2));

      // combined summary
      setSummary(payload.summaries[0] || "");
      // detailed extraction
      setDetail(payload.summaries_detailed || null);
      
      // NUEVO: Extraer nombre del juez de extracted_data si no viene en extracted_judge_name
      let judgeNameFromData = payload.extracted_judge_name;
      
      if (!judgeNameFromData && payload.extracted_data) {
        // Buscar en extracted_data
        const possibleKeys = ["Nombre del juez", "nombre del juez", "juez", "Juez"];
        for (const key of possibleKeys) {
          if (payload.extracted_data[key]) {
            judgeNameFromData = payload.extracted_data[key];
            console.log(`✅ Nombre del juez encontrado en extracted_data[${key}]:`, judgeNameFromData);
            break;
          }
        }
      }
      
      // Información del juez y análisis predictivo
      setJudgeProfile(payload.judge_profile || null);
      setExtractedJudgeName(judgeNameFromData || null);
      setPredictiveAnalysis(payload.predictive_analysis || null);

    } catch (err) {
      setError("❌ " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Componente para la sección de resumen
  const renderResumenSection = () => {
    if (!summary) return null;

    return (
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>📄 Resumen</h2>
        <div style={{ 
          background: "white", 
          padding: 15, 
          borderRadius: 6, 
          fontSize: "14px",
          lineHeight: 1.6,
          whiteSpace: "pre-wrap",
          border: "1px solid #e0e0e0"
        }}>
          {summary}
        </div>
      </div>
    );
  };

  // Componente para la sección de datos extraídos
  const renderDatosExtraidosSection = () => {
    if (!detail) return null;

    return (
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>📋 Datos extraídos ({OPCIONES_INSTANCIA[instanciaId]})</h2>
        {detail.map((doc, idx) => (
          <div key={idx} style={{ marginBottom: 15 }}>
            <div style={{ 
              background: "white", 
              padding: 15, 
              borderRadius: 6,
              border: "1px solid #e0e0e0"
            }}>
              <pre style={{ 
                margin: 0,
                fontSize: "14px",
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
                fontFamily: "inherit"
              }}>
                {doc.summary}
              </pre>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Componente para mostrar hallazgos del juez
  const renderHallazgosJuezSection = () => {
    // Solo mostrar si hay información relevante
    if (!extractedJudgeName && !judgeProfile && !predictiveAnalysis) {
      return null;
    }

    return (
      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>🔍 Hallazgos del Juez</h2>
        
        {/* Debug info comentado para producción */}
        {/*
        <div style={{ 
          background: "#ffe5e5", 
          padding: 10, 
          fontSize: "12px", 
          marginBottom: 15,
          borderRadius: 4,
          border: "1px solid #ffb3b3"
        }}>
          <strong>🐛 DEBUG INFO:</strong><br/>
          extractedJudgeName: {extractedJudgeName || "null"}<br/>
          judgeProfile: {judgeProfile ? "✅ Presente" : "❌ Null"}<br/>
          predictiveAnalysis: {predictiveAnalysis ? "✅ Presente" : "❌ Null"}<br/>
          {predictiveAnalysis && (
            <>
              predictiveAnalysis.juez_encontrado: {predictiveAnalysis.juez_encontrado?.toString()}<br/>
              predictiveAnalysis.nombre_juez: {predictiveAnalysis.nombre_juez}<br/>
              predictiveAnalysis.probabilidad_exito: {predictiveAnalysis.probabilidad_exito}
            </>
          )}
        </div>
        */}
        
        {/* Nombre del juez extraído del documento */}
        {extractedJudgeName && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ 
              background: "white", 
              padding: 15, 
              borderRadius: 6,
              border: "1px solid #e0e0e0"
            }}>
              <strong style={{ color: "#2196F3", fontSize: "15px" }}>📋 Juez extraído del documento:</strong>
              <div style={{ 
                background: "#e3f2fd", 
                padding: 12, 
                borderRadius: 6, 
                marginTop: 8,
                fontWeight: "bold",
                fontSize: "16px",
                color: "#1565c0"
              }}>
                {extractedJudgeName}
              </div>
            </div>
          </div>
        )}

        {/* Análisis Predictivo */}
        {predictiveAnalysis ? (
          <div>
            {/* Estado del juez en BD */}
            <div style={{ 
              background: predictiveAnalysis.juez_encontrado ? "#e8f5e8" : "#fff3e0", 
              padding: 12, 
              borderRadius: 6, 
              marginBottom: 20,
              border: `2px solid ${predictiveAnalysis.juez_encontrado ? "#4caf50" : "#ff9800"}`
            }}>
              <strong style={{ color: predictiveAnalysis.juez_encontrado ? "#2e7d32" : "#e65100", fontSize: "14px" }}>
                {predictiveAnalysis.juez_encontrado 
                  ? "✅ Juez encontrado en base de datos" 
                  : "⚠️ Juez no encontrado exacto, pero se encontró similitud"}
              </strong>
            </div>

            {/* Grid principal con información clave */}
            <div style={{ 
              display: "grid", 
              gridTemplateColumns: "1fr 1fr", 
              gap: 20,
              marginBottom: 20
            }}>
              {/* Columna izquierda */}
              <div>
                {/* Nombre de juez */}
                <div style={{ 
                  background: "white", 
                  padding: 15, 
                  borderRadius: 8, 
                  border: "1px solid #ddd",
                  marginBottom: 15
                }}>
                  <h4 style={{ margin: "0 0 8px 0", color: "#2196F3", fontSize: "14px", fontWeight: "bold" }}>
                    👨‍⚖️ Nombre de juez:
                  </h4>
                  <p style={{ margin: 0, fontWeight: "bold", color: "#333", fontSize: "15px" }}>
                    {predictiveAnalysis.nombre_juez || "No disponible"}
                  </p>
                </div>

                {/* Probabilidad de éxito */}
                <div style={{ 
                  background: "white", 
                  padding: 15, 
                  borderRadius: 8, 
                  border: "1px solid #ddd"
                }}>
                  <h4 style={{ margin: "0 0 8px 0", color: "#ff9800", fontSize: "14px", fontWeight: "bold" }}>
                    📊 Probabilidad de éxito:
                  </h4>
                  <p style={{ margin: 0, fontWeight: "bold", color: "#ff9800", fontSize: "24px" }}>
                    {predictiveAnalysis.probabilidad_exito || "No determinada"}
                  </p>
                </div>
              </div>

              {/* Columna derecha */}
              <div>
                {/* Número de casos similares */}
                <div style={{ 
                  background: "white", 
                  padding: 15, 
                  borderRadius: 8, 
                  border: "1px solid #ddd"
                }}>
                  <h4 style={{ margin: "0 0 8px 0", color: "#4caf50", fontSize: "14px", fontWeight: "bold" }}>
                    📈 Número de casos similares resueltos:
                  </h4>
                  <p style={{ margin: 0, fontSize: "13px", lineHeight: 1.5, color: "#555" }}>
                    {predictiveAnalysis.casos_similares || "No disponible"}
                  </p>
                  <small style={{ color: "#666", fontSize: "11px" }}>
                    (Fundado, Infundado, Improcedente)
                  </small>
                </div>
              </div>
            </div>

            {/* Postura del juez - Sección completa */}
            <div style={{ 
              background: "white", 
              padding: 18, 
              borderRadius: 8, 
              border: "1px solid #ddd",
              marginBottom: 20
            }}>
              <h4 style={{ margin: "0 0 12px 0", color: "#673ab7", fontSize: "15px", fontWeight: "bold" }}>
                ⚖️ Postura del juez sobre el caso específico de la materia:
              </h4>
              <p style={{ margin: "0 0 10px 0", fontSize: "14px", lineHeight: 1.6, color: "#555" }}>
                {predictiveAnalysis.postura_juez || "Información insuficiente"}
              </p>
              <div style={{ 
                background: "#f3e5f5", 
                padding: 10, 
                borderRadius: 4, 
                marginTop: 10,
                borderLeft: "4px solid #673ab7"
              }}>
                <small style={{ color: "#4a148c", fontStyle: "italic", fontSize: "12px" }}>
                  💡 Conceptos jurídicos más relevantes para este juez y las razones/argumentos que usa para decidir Fundado, Infundado o Improcedente
                </small>
              </div>
            </div>

            {/* Medios probatorios valorados */}
            {predictiveAnalysis.medios_probatorios_recomendados && 
             predictiveAnalysis.medios_probatorios_recomendados !== "No disponible" && (
              <div style={{ 
                background: "white", 
                padding: 18, 
                borderRadius: 8, 
                border: "1px solid #ddd",
                marginBottom: 20
              }}>
                <h4 style={{ margin: "0 0 12px 0", color: "#795548", fontSize: "15px", fontWeight: "bold" }}>
                  🔍 Medios probatorios que valora este juez:
                </h4>
                <p style={{ margin: "0 0 10px 0", fontSize: "14px", lineHeight: 1.6, color: "#555" }}>
                  {predictiveAnalysis.medios_probatorios_recomendados}
                </p>
                <div style={{ 
                  background: "#efebe9", 
                  padding: 10, 
                  borderRadius: 4, 
                  marginTop: 10,
                  borderLeft: "4px solid #795548"
                }}>
                  <small style={{ color: "#3e2723", fontStyle: "italic", fontSize: "12px" }}>
                    📋 Pericias y medios probatorios que se han repetido en casos ganados. Este juez valora mucho que se adjunte este tipo de evidencia.
                  </small>
                </div>
              </div>
            )}

            {/* Estadísticas del distrito judicial */}
            {predictiveAnalysis.estadisticas_distrito && 
             predictiveAnalysis.estadisticas_distrito !== "No disponible" && (
              <div style={{ 
                background: "white", 
                padding: 18, 
                borderRadius: 8, 
                border: "1px solid #ddd"
              }}>
                <h4 style={{ margin: "0 0 12px 0", color: "#e91e63", fontSize: "15px", fontWeight: "bold" }}>
                  📊 Estadística judicial del distrito:
                </h4>
                <p style={{ margin: "0 0 10px 0", fontSize: "14px", lineHeight: 1.6, color: "#555" }}>
                  {predictiveAnalysis.estadisticas_distrito}
                </p>
                <div style={{ 
                  background: "#fce4ec", 
                  padding: 10, 
                  borderRadius: 4, 
                  marginTop: 10,
                  borderLeft: "4px solid #e91e63"
                }}>
                  <small style={{ color: "#880e4f", fontStyle: "italic", fontSize: "12px" }}>
                    🏛️ Tendencia del distrito judicial: ¿Es más pro-empresa o pro-trabajador? Análisis basado en datos históricos.
                  </small>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ 
            background: "white", 
            padding: 15, 
            borderRadius: 6, 
            border: "1px solid #e0e0e0",
            textAlign: "center",
            color: "#666"
          }}>
            <strong>ℹ️ Procesando análisis del juez...</strong>
            <p style={{ margin: "10px 0 0 0", fontSize: "14px" }}>
              La información del análisis predictivo se cargará cuando esté disponible.
            </p>
          </div>
        )}

        {/* Información adicional del perfil si está disponible */}
        {judgeProfile && (
          <div style={{ 
            marginTop: 25, 
            paddingTop: 20, 
            borderTop: "2px solid #e0e0e0",
            background: "white",
            padding: "20px",
            borderRadius: "8px"
          }}>
            <h4 style={{ margin: "0 0 15px 0", color: "#607d8b", fontSize: "16px" }}>
              📝 Información Complementaria del Juez:
            </h4>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              <div>
                <div style={{ marginBottom: 15 }}>
                  <strong style={{ color: "#455a64" }}>Total de casos:</strong><br />
                  <span style={{ color: "#ff9800", fontWeight: "bold", fontSize: "18px" }}>
                    {judgeProfile.casos_totales}
                  </span>
                </div>
              </div>

              <div>
                {judgeProfile.tribunales && judgeProfile.tribunales !== "No disponible" && (
                  <div style={{ marginBottom: 15 }}>
                    <strong style={{ color: "#455a64" }}>Tribunales:</strong><br />
                    <span style={{ fontSize: "14px", color: "#666" }}>{judgeProfile.tribunales}</span>
                  </div>
                )}
                {judgeProfile.especialidades && judgeProfile.especialidades !== "No disponible" && (
                  <div style={{ marginBottom: 15 }}>
                    <strong style={{ color: "#455a64" }}>Especialidades:</strong><br />
                    <span style={{ fontSize: "14px", color: "#666" }}>{judgeProfile.especialidades}</span>
                  </div>
                )}
              </div>
            </div>

            {judgeProfile.perfil_juez && judgeProfile.perfil_juez !== "Perfil no disponible" && (
              <div style={{ marginTop: 20 }}>
                <strong style={{ color: "#455a64" }}>Perfil Profesional:</strong>
                <div style={{ 
                  background: "#f5f5f5", 
                  padding: 15, 
                  borderRadius: 6, 
                  marginTop: 8,
                  fontSize: "14px",
                  lineHeight: 1.6,
                  color: "#333"
                }}>
                  {judgeProfile.perfil_juez}
                </div>
              </div>
            )}

            {judgeProfile.similarity_score && judgeProfile.similarity_score < 1.0 && (
              <div style={{ 
                marginTop: 15,
                fontSize: "13px", 
                color: "#666", 
                fontStyle: "italic",
                background: "#f9f9f9",
                padding: "8px 12px",
                borderRadius: "4px",
                border: "1px solid #e0e0e0"
              }}>
                🔍 Coincidencia por similitud: {Math.round(judgeProfile.similarity_score * 100)}% - Tipo: {judgeProfile.match_type}
              </div>
            )}
          </div>
        )}

        {/* Mensaje cuando no hay información */}
        {!judgeProfile && !predictiveAnalysis && extractedJudgeName && (
          <div style={{ 
            background: "white", 
            padding: 15, 
            borderRadius: 6, 
            border: "1px solid #e0e0e0",
            textAlign: "center"
          }}>
            <strong style={{ color: "#e65100", fontSize: "15px" }}>
              ⚠️ No se encontró información del juez en la base de datos
            </strong>
            <div style={{ marginTop: 8, fontSize: "14px", color: "#666" }}>
              Nombre buscado: "<em>{extractedJudgeName}</em>"
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: 800, margin: "auto", padding: 20, fontFamily: "sans-serif" }}>
      <h1>Sube y resume N PDFs</h1>

      <div style={{ margin: "20px 0" }}>
        <label htmlFor="instancia"><strong>Selecciona una instancia:</strong></label>
        <br />
        <select
          id="instancia"
          value={instanciaId}
          onChange={(e) => setInstanciaId(e.target.value)}
          style={{ padding: 5, width: "100%", marginTop: 5 }}
        >
          <option value="">-- Selecciona una opción --</option>
          {Object.entries(OPCIONES_INSTANCIA).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
      </div>

      <div>
        <input type="file" accept="application/pdf" multiple onChange={handleFileChange} />
        <button 
          onClick={onSubmit} 
          disabled={loading} 
          style={{ marginLeft: 10 }}
        >
          {loading ? "Procesando…" : "Subir y resumir"}
        </button>
      </div>

      {error && <p style={{ color: "crimson", marginTop: 20 }}><strong>{error}</strong></p>}

      {/* ORDEN REQUERIDO: */}
      {/* 1. Resumen */}
      {renderResumenSection()}

      {/* 2. Datos extraídos */}
      {renderDatosExtraidosSection()}

      {/* 3. Hallazgos del Juez */}
      {renderHallazgosJuezSection()}
    </div>
  );
}